import React, { useState } from 'react'
import {
  Settings, Users, Bell, Shield, Calendar, School, Monitor, CreditCard,
  ChevronRight, ClipboardList, TrendingUp, History, MinusCircle,
  RefreshCw, BarChart3, DollarSign, MessageSquare, ChevronDown, FileText, Palette, Link2
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import UserManagementComplete from './UserManagementComplete'
import { CalendarSubscription } from './CalendarSubscription'
import { CalendarSettings } from './admin/CalendarSettings'
import GoogleAuthIntegration from './GoogleAuthIntegration'
import NotificationSettings from './NotificationSettings'
import SecuritySettings from './SecuritySettings'
import GeneralSettings from './GeneralSettings'
import SchoolBrowser from './SchoolBrowser'
import { KioskDeviceManager } from './admin/KioskDeviceManager'
import { SubscriptionSettings } from './subscription/SubscriptionSettings'
// New imports for classic features
import { AssignmentHistory } from './AssignmentHistory'
import { EnhancedDeductionsManager } from './EnhancedDeductionsManager'
import { RotationGroupsManager } from './admin/RotationGroupsManager'
import WeeklySummary from './WeeklySummary'
import PerformanceTrends from './PerformanceTrends'
import KidRequests from './KidRequests'
import { GreenlightConverter } from './GreenlightConverter'
import { AuditLogs as ActivityFeed } from './admin/AuditLogs'
import { FamilyFeaturesManager } from './FamilyFeaturesManager'
import { KidPasswordAuthSettings } from './KidPasswordAuthSettings'
import { ToggleLeft, Key, HelpCircle } from 'lucide-react'
import { SessionsView } from './SessionsView'
import KioskSettings from './KioskSettings'
import SupportSettings from './SupportSettings'
import { ConnectedApps } from './ConnectedApps'

type SettingsSection =
  | 'family-users' | 'family-kiosks' | 'family-kiosk-display' | 'family-sessions'
  | 'chores-history' | 'chores-deductions' | 'chores-rotation' | 'chores-audit'
  | 'calendar-settings' | 'calendar-schools'
  | 'analytics-weekly' | 'analytics-trends' | 'analytics-greenlight'
  | 'notifications-settings' | 'notifications-requests'
  | 'support'
  | 'system-general' | 'system-security' | 'system-subscription' | 'system-features' | 'system-kid-auth'
  | 'connected-apps'

interface SettingsGroup {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  items: {
    id: SettingsSection
    label: string
    icon: React.ComponentType<{ className?: string }>
  }[]
}

export default function SystemSettings() {
  const { user } = useAuth()
  const isParent = user?.is_parent ?? false
  const [activeSection, setActiveSection] = useState<SettingsSection>(isParent ? 'family-users' : 'notifications-settings')
  const [expandedGroups, setExpandedGroups] = useState<string[]>(isParent ? ['family', 'chores', 'analytics'] : ['notifications'])

  const handleSectionClick = (sectionId: SettingsSection) => {
    setActiveSection(sectionId)
  }

  // Greenlight is a per-family feature toggle (family_feature_toggles.greenlight)
  const { isFeatureEnabled } = useFamilyFeatures()
  const greenlightEnabled = isFeatureEnabled('greenlight')

  // Parent-only settings groups
  const parentSettingsGroups: SettingsGroup[] = [
    {
      id: 'family',
      label: 'Family Management',
      icon: Users,
      items: [
        { id: 'family-users', label: 'Users & Places', icon: Users },
        { id: 'family-kiosks', label: 'Devices', icon: Monitor },
        { id: 'family-kiosk-display', label: 'Kiosk Display', icon: Palette },
        { id: 'family-sessions', label: 'Active Sessions', icon: Shield }
      ]
    },
    {
      id: 'chores',
      label: 'Chore Administration',
      icon: ClipboardList,
      items: [
        { id: 'chores-history', label: 'Assignment History', icon: History },
        { id: 'chores-deductions', label: 'Deductions', icon: MinusCircle },
        { id: 'chores-rotation', label: 'Rotation Groups', icon: RefreshCw },
        { id: 'chores-audit', label: 'Audit Log', icon: FileText }
      ]
    },
    {
      id: 'calendar',
      label: 'Calendar & Schools',
      icon: Calendar,
      items: [
        { id: 'calendar-settings', label: 'Calendar Integration', icon: Calendar },
        { id: 'calendar-schools', label: 'Schools', icon: School }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics & Reports',
      icon: BarChart3,
      items: [
        { id: 'analytics-weekly', label: 'Weekly Summary', icon: TrendingUp },
        { id: 'analytics-trends', label: 'Performance Trends', icon: BarChart3 },
        ...(greenlightEnabled ? [{ id: 'analytics-greenlight' as SettingsSection, label: 'Greenlight Converter', icon: DollarSign }] : [])
      ]
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      items: [
        { id: 'notifications-settings', label: 'Notification Settings', icon: Bell },
        { id: 'notifications-requests', label: 'Kid Requests', icon: MessageSquare }
      ]
    },
    {
      id: 'support',
      label: 'Help & Support',
      icon: HelpCircle,
      items: [
        { id: 'support', label: 'Feedback & Support', icon: HelpCircle }
      ]
    },
    {
      id: 'connected',
      label: 'Schedule Sync',
      icon: Link2,
      items: [
        { id: 'connected-apps', label: 'OurTurn Connection', icon: Link2 }
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Settings,
      items: [
        { id: 'system-subscription', label: 'Subscription', icon: CreditCard },
        { id: 'system-features', label: 'Features', icon: ToggleLeft },
        { id: 'system-kid-auth', label: 'Kid Passwords', icon: Key },
        { id: 'system-general', label: 'General', icon: Settings },
        { id: 'system-security', label: 'Security', icon: Shield }
      ]
    }
  ]

  // Child-only settings - just notifications
  const childSettingsGroups: SettingsGroup[] = [
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      items: [
        { id: 'notifications-settings', label: 'Notification Settings', icon: Bell }
      ]
    }
  ]

  const settingsGroups = isParent ? parentSettingsGroups : childSettingsGroups

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  const getCurrentGroupAndItem = () => {
    for (const group of settingsGroups) {
      const item = group.items.find(item => item.id === activeSection)
      if (item) return { group, item }
    }
    return null
  }

  const current = getCurrentGroupAndItem()

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
          <p className="text-gray-500 mt-1">
            {isParent ? 'Manage your family chore system configuration' : 'Manage your preferences'}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Sidebar Navigation - Desktop */}
          <nav className="hidden lg:block w-72 border-r border-gray-100 p-4 space-y-1">
            {settingsGroups.map((group) => {
              const GroupIcon = group.icon
              const isExpanded = expandedGroups.includes(group.id)
              const hasActiveItem = group.items.some(item => item.id === activeSection)

              return (
                <div key={group.id} className="space-y-1">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasActiveItem
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <GroupIcon className="w-4 h-4" />
                      {group.label}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="ml-4 space-y-1">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon
                        const isActive = item.id === activeSection

                        return (
                          <button
                            key={item.id}
                            onClick={() => handleSectionClick(item.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <ItemIcon className="w-4 h-4" />
                            {item.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>

          {/* Mobile Navigation - Accordion */}
          <div className="lg:hidden border-b border-gray-100">
            <div className="p-4 space-y-2">
              {settingsGroups.map((group) => {
                const GroupIcon = group.icon
                const isExpanded = expandedGroups.includes(group.id)
                const hasActiveItem = group.items.some(item => item.id === activeSection)

                return (
                  <div key={group.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${
                        hasActiveItem ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <GroupIcon className="w-4 h-4" />
                        {group.label}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="bg-white border-t border-gray-100">
                        {group.items.map((item) => {
                          const ItemIcon = item.icon
                          const isActive = item.id === activeSection

                          return (
                            <button
                              key={item.id}
                              onClick={() => handleSectionClick(item.id)}
                              className={`w-full flex items-center gap-2 px-4 py-3 text-sm border-b border-gray-50 last:border-0 ${
                                isActive
                                  ? 'bg-blue-50 text-blue-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4" />
                              {item.label}
                              {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 p-6 min-h-[600px]">
            {/* Breadcrumb */}
            {current && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <span>{current.group.label}</span>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium">{current.item.label}</span>
              </div>
            )}

            {/* Family Management */}
            {activeSection === 'family-users' && <UserManagementComplete />}
            {activeSection === 'family-kiosks' && <KioskDeviceManager />}
            {activeSection === 'family-kiosk-display' && <KioskSettings />}
            {activeSection === 'family-sessions' && <SessionsView />}

            {/* Chore Administration */}
            {activeSection === 'chores-history' && <AssignmentHistory />}
            {activeSection === 'chores-deductions' && <EnhancedDeductionsManager />}
            {activeSection === 'chores-rotation' && <RotationGroupsManager />}
            {activeSection === 'chores-audit' && <ActivityFeed />}

            {/* Calendar & Schools */}
            {activeSection === 'calendar-settings' && (
              <div className="space-y-6">
                <CalendarSettings />
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Calendar Integration</h3>
                  <div className="space-y-6">
                    <GoogleAuthIntegration />
                    <CalendarSubscription />
                  </div>
                </div>
              </div>
            )}
            {activeSection === 'calendar-schools' && <SchoolBrowser />}

            {/* Analytics & Reports */}
            {activeSection === 'analytics-weekly' && <WeeklySummary />}
            {activeSection === 'analytics-trends' && <PerformanceTrends />}
            {activeSection === 'analytics-greenlight' && greenlightEnabled && <GreenlightConverter />}

            {/* Notifications */}
            {activeSection === 'notifications-settings' && <NotificationSettings />}
            {activeSection === 'notifications-requests' && <KidRequests />}

            {/* Support */}
            {activeSection === 'support' && <SupportSettings />}

            {/* Connected Apps */}
            {activeSection === 'connected-apps' && <ConnectedApps />}

            {/* System */}
            {activeSection === 'system-subscription' && <SubscriptionSettings />}
            {activeSection === 'system-features' && <FamilyFeaturesManager />}
            {activeSection === 'system-kid-auth' && <KidPasswordAuthSettings />}
            {activeSection === 'system-general' && <GeneralSettings />}
            {activeSection === 'system-security' && <SecuritySettings />}
          </div>
        </div>
      </div>
    </div>
  )
}